import { useTranslation } from "react-i18next";
import { LoadingSpinner } from "../ui/LoadingSpinner/LoadingSpinner";
import { ErrorFallback } from "../ui/ErrorFallback/ErrorFallback";
import { useSalesDocDetail, type SalesDocKind } from "../../hooks/use-orders";
import { formatAmount, formatDate, formatQuantity } from "../../lib/date";
import styles from "./SalesDocDetailView.module.css";

const LABEL_KEYS: Record<SalesDocKind, { docNo: string; status: string; dueDate: string }> = {
	delivery: { docNo: "deliveryNo", status: "deliveryStatus", dueDate: "deliveryDate" },
	invoice: { docNo: "invoiceNo", status: "invoiceStatus", dueDate: "paymentDueDate" },
};

interface SalesDocDetailViewProps {
	orderId: string;
	kind: SalesDocKind;
	docEntry: number;
	url?: string;
}

export function SalesDocDetailView({ orderId, kind, docEntry, url }: SalesDocDetailViewProps) {
	const { t } = useTranslation();
	const query = useSalesDocDetail(orderId, kind, docEntry, url);
	const labels = LABEL_KEYS[kind];
	const d = query.data;

	if (query.isLoading) return <LoadingSpinner />;
	if (query.isError) return <ErrorFallback onRetry={query.refetch} />;
	if (!d) return null;

	const cur = d.docCur ?? "";
	const statusLabel = d.canceled ? t("cancelled") : d.docStatus === "C" ? t("closed") : t("open");

	return (
		<div className={styles.root}>
			<div className={styles.header}>
				<dl className={styles.fields}>
					<dt>{t("counterpart")}</dt>
					<dd>
						<strong>{d.cardCode}</strong> — {d.cardName}
					</dd>
					<dt>{t("reference")}</dt>
					<dd>{d.numAtCard || "—"}</dd>
					<dt>{t("salesPerson")}</dt>
					<dd>{d.salesPerson || "—"}</dd>
					<dt>{t("shippingAddress")}</dt>
					<dd className={styles.multiline}>{d.shipTo || "—"}</dd>
				</dl>
				<dl className={styles.fields}>
					<dt>{t(labels.docNo)}</dt>
					<dd>
						<strong>{d.docNum}</strong>
					</dd>
					<dt>{t(labels.status)}</dt>
					<dd>
						<span
							className={[
								styles.status,
								d.canceled
									? styles.statusCanceled
									: d.docStatus === "C"
										? styles.statusClosed
										: styles.statusOpen,
							].join(" ")}
						>
							{statusLabel}
						</span>
					</dd>
					<dt>{t("postingDate")}</dt>
					<dd>{formatDate(d.docDate)}</dd>
					<dt>{t(labels.dueDate)}</dt>
					<dd>{formatDate(d.docDueDate)}</dd>
					<dt>{t("documentDate")}</dt>
					<dd>{formatDate(d.taxDate)}</dd>
				</dl>
			</div>

			<div className={styles.tableWrap}>
				<table className={styles.table}>
					<thead>
						<tr>
							<th>#</th>
							<th>{t("itemCode")}</th>
							<th>{t("description")}</th>
							<th className={styles.num}>{t("quantity")}</th>
							<th className={styles.num}>{t("unitPrice")}</th>
							<th className={styles.num}>{t("discountPercent")}</th>
							<th>{t("vatRate")}</th>
							<th className={styles.num}>{t("lineTotal")}</th>
							<th>{t("warehouse")}</th>
						</tr>
					</thead>
					<tbody>
						{d.lines.map((l, i) => (
							<tr key={l.lineNum}>
								<td>{i + 1}</td>
								<td className={styles.code}>{l.itemCode}</td>
								<td>{l.itemName}</td>
								<td className={styles.num}>
									{formatQuantity(l.quantity)}
									{l.unitMsr ? ` ${l.unitMsr}` : ""}
								</td>
								<td className={styles.num}>
									{formatAmount(l.price)} {l.currency ?? ""}
								</td>
								<td className={styles.num}>{formatQuantity(l.discountPercent)}</td>
								<td>{l.vatGroup ?? "—"}</td>
								<td className={styles.num}>
									{formatAmount(l.lineTotal)} {cur}
								</td>
								<td>{l.warehouseCode ?? "—"}</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>

			<div className={styles.footer}>
				<div className={styles.comments}>
					<div className={styles.label}>{t("notes")}</div>
					<div className={styles.multiline}>{d.comments || "—"}</div>
				</div>
				<dl className={styles.totals}>
					<dt>{t("subtotal")}</dt>
					<dd>
						{formatAmount(d.totalBeforeDiscount)} {cur}
					</dd>
					{d.discountTotal !== 0 && (
						<>
							<dt>
								{t("discount")} ({formatQuantity(d.discountPercent)}%)
							</dt>
							<dd>
								{formatAmount(d.discountTotal)} {cur}
							</dd>
						</>
					)}
					<dt>{t("vatTotal")}</dt>
					<dd>
						{formatAmount(d.vatTotal)} {cur}
					</dd>
					{d.withholdingTotal !== 0 && (
						<>
							<dt>{t("withholdingTotal")}</dt>
							<dd>
								{formatAmount(d.withholdingTotal)} {cur}
							</dd>
						</>
					)}
					<dt className={styles.grand}>{t("total")}</dt>
					<dd className={styles.grand}>
						{formatAmount(d.docTotal)} {cur}
					</dd>
				</dl>
			</div>
		</div>
	);
}
